import { useState, useEffect } from 'react'
import { MedicationReference } from '../types/medication'
import { formularyService } from '../services/formularyService'
import './AlternativeMedications.css'

interface AlternativeMedicationsProps {
  medication: {
    name: string
    referenceDrugId?: string
    atcCodes?: string[]
  }
  onSelectAlternative: (medication: MedicationReference) => void
  showBadge?: boolean
}

export function AlternativeMedications({
  medication,
  onSelectAlternative,
  showBadge = true,
}: AlternativeMedicationsProps) {
  const [alternatives, setAlternatives] = useState<MedicationReference[]>([])
  const [loading, setLoading] = useState(false)
  const [isInFormulary, setIsInFormulary] = useState<boolean | null>(null)

  useEffect(() => {
    const fetchAlternatives = async () => {
      if (!medication.referenceDrugId) {
        setAlternatives([])
        setIsInFormulary(null)
        return
      }

      setLoading(true)
      try {
        // Check if medication is in formulary
        const formularyCheck = await formularyService.checkFormulary(medication.referenceDrugId)
        setIsInFormulary(formularyCheck.inFormulary)

        // If not in formulary, get formulary medications as alternatives
        if (!formularyCheck.inFormulary) {
          // Note: formularyMeds contains medicationId references, not full MedicationReference objects
          // We would need to fetch medication references to compare ATC codes
          // For now, just show empty array since we don't have MedicationReference objects from formulary
          // TODO: Fetch medication references and filter by ATC code to show relevant alternatives
          const filtered: MedicationReference[] = []
          setAlternatives(filtered.slice(0, 5)) // Limit to 5 suggestions
        } else {
          setAlternatives([])
        }
      } catch (err) {
        console.error('Failed to fetch alternatives', err)
        setAlternatives([])
        setIsInFormulary(null)
      } finally {
        setLoading(false)
      }
    }

    fetchAlternatives()
  }, [medication.referenceDrugId, medication.atcCodes])

  if (loading) {
    return (
      <div className="alternative-medications">
        <div className="loading">Checking formulary...</div>
      </div>
    )
  }

  if (isInFormulary === true) {
    if (!showBadge) {
      return null
    }
    return (
      <div className="alternative-medications">
        <div className="formulary-badge formulary-in">
          ✓ This medication is in the practice formulary
        </div>
      </div>
    )
  }

  if (isInFormulary === false && alternatives.length === 0) {
    return (
      <div className="alternative-medications">
        {showBadge && (
          <div className="formulary-badge formulary-out">
            ⚠ This medication is not in the practice formulary
          </div>
        )}
        <div className="no-alternatives">No formulary alternatives available</div>
      </div>
    )
  }

  if (alternatives.length === 0) {
    return null
  }

  return (
    <div className="alternative-medications">
      {showBadge && (
        <div className="formulary-badge formulary-out">
          ⚠ This medication is not in the practice formulary
        </div>
      )}
      <div className="alternatives-header">
        <strong>Formulary Alternatives:</strong>
      </div>
      <ul className="alternatives-list">
        {alternatives.map((alt) => (
          <li key={alt.drugIdPK} className="alternative-item">
            <div className="alternative-name">{alt.productName}</div>
            <div className="alternative-details">
              {alt.dosageForm} {alt.strength && `- ${alt.strength}`}
              {alt.gmsReimbursable && <span className="gms-badge">GMS</span>}
            </div>
            <button
              type="button"
              className="btn-link btn-small"
              onClick={() => onSelectAlternative(alt)}
            >
              Use this instead
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}
